import { useMutation } from '@apollo/client';
import {
  ISlackConnectInput,
  ISlackConnectMutation,
  ISlackConnectMutationVariables,
  ISlackDestinationFieldsFragment,
  SlackEntityType,
  SlackSourceType,
} from '../../utils/apollo/graphqlSchema';
import printGraphqlErrors from '../../utils/apollo/printGraphqlErrors';
import { useCallback } from 'react';
import { assertNotNil, GraphQLID } from '../../utils/types';
import { SLACK_CONNECT } from '../../utils/apollo/slack/mutations';
import { toSlackDestinationInput } from './utils';

const useSlackConnect = () => {
  const [executeSlackConnect, { loading }] = useMutation<
    ISlackConnectMutation,
    ISlackConnectMutationVariables
  >(SLACK_CONNECT);

  const connectSlack = useCallback(
    async (input: ISlackConnectInput) => {
      const response = await executeSlackConnect({ variables: { input } });
      const result = response?.data?.slackConnect;
      if (printGraphqlErrors(result?.errors)) {
        return null;
      }
      return result?.entity?.destination ?? null;
    },
    [executeSlackConnect],
  );

  const connectDashboard = useCallback(
    async (
      dashboardId: GraphQLID,
      destination: ISlackDestinationFieldsFragment,
    ) => {
      const destinationInput = toSlackDestinationInput(destination);
      assertNotNil(destinationInput);
      return await connectSlack({
        source: {
          type: SlackSourceType.Entity,
          entitySource: {
            entityType: SlackEntityType.Dashboard,
            entityId: dashboardId,
          },
        },
        destination: destinationInput,
      });
    },
    [connectSlack],
  );

  return { connectDashboard, connectLoading: loading };
};

export default useSlackConnect;
